import React, { useRef, useEffect, useState, useMemo } from "react";
import { Loader, ShoppingCart, Check, X, ShoppingBag } from "react-feather";
import Button from "../Button";
import {
    computeOutOfStockItems,
    groupProductAndIngredientsByItem,
} from "../../Helpers/util";
import { fetchStocks } from "../../Api/ingredientsApi";
import ReorderPopup from "./Popup";
import { PopupContainerProps } from "./types";
import i18next from "i18next";
import { dispatchLocationChange } from "~/assets/js/Api/storeApi";
import { wait } from "~/assets/js/Helpers/util";
import { useCartContext } from "~/assets/js/global/cartContext";
import AddressPopup from "../AddressPopup";
import useGroupOrder from "../../global/useGroupOrder";

export const REORDER_STATES = {
    CHECKING_STOCK: {
        type: "stockCheck",
        icon: Loader,
    },
    FILLING_BASKET: {
        type: "basket",
        icon: ShoppingCart,
    },
    FINISHED: {
        type: "finished",
        icon: Check,
    },
    ERROR: {
        type: "error",
        icon: X,
    },
};

const ALLOW_ABORT_DELAY = 3000;

const ReorderContainer = ({ order, store_id = 0, buttonStyle = "" }) => {
    const { actions } = useCartContext();
    const [open, setOpen] = useState(false);
    const [reoderAllowed, setAllowReorder] = useState(false);
    const [isStoreInit, setStoreInit] = useState(false);
    const [addressOverlayOpen, setOverlayOpen] = useState(false);
    const { isGroupOrder } = useGroupOrder();

    const [reorderState, setReorderState] = useState(
        REORDER_STATES.CHECKING_STOCK
    );

    const isAborted = useRef(false);

    useEffect(() => {
        const onInit = async () => {
            if (!store_id) {
                setStoreInit(false);
                setAllowReorder(true); // will prompt store selection
                return;
            }

            setStoreInit(true);

            // do not recheck the stocks if popup is still open
            if (open) {
                return;
            }

            const reorder = await checkIsReorderPossible();
            setAllowReorder(reorder.isPossible);
        };
        onInit();
    }, [store_id]);

    const checkIsReorderPossible = async (store = null) => {
        const { items } = order; // item is a list of all order items
        // Transform from order item to cart item products
        let products = items;
        const { groupedData, iDs } = groupProductAndIngredientsByItem(products);

        //const areAllProductsInWPStore = ensureNoProductIsMissing(iDs);

        //if (!areAllProductsInWPStore) {
        //    return { isPossible: false, products };
        //}

        const storeId = store !== null ? store : store_id;

        const stocks = await fetchStocks(iDs, storeId);

        if (!stocks.ok) {
            return { isPossible: false, products: [] };
        }

        const outOfStockItems = computeOutOfStockItems(
            groupedData,
            stocks.data
        );

        if (isGroupOrder) {
            const participant = window.cookie.get()?.participant || {};
            const { uid, name } = participant;
            products = products.map((prod) => ({
                ...prod,
                owner_uid: uid,
                owner_name: name,
            }));
        }

        // We only allow full reorder so the oos items must be empty
        return { isPossible: !outOfStockItems.length, products };
    };

    const handleReorder = async (store = null) => {
        isAborted.current = false;
        setReorderState(REORDER_STATES.CHECKING_STOCK);
        setOpen(true);

        const reorder = await checkIsReorderPossible(store);

        if (!reorder.isPossible) {
            setAllowReorder(false);
            return setReorderState(REORDER_STATES.ERROR);
        }

        // wait atleast three second to give customer time to abort
        await wait(ALLOW_ABORT_DELAY);

        // The process was aborted
        if (isAborted.current) {
            isAborted.current = false;
            return;
        }

        setReorderState(REORDER_STATES.FILLING_BASKET);

        const response = await actions.addMultipleItemsToCart(reorder.products);

        if (!response.ok) {
            return setReorderState(REORDER_STATES.ERROR);
        }

        setReorderState(REORDER_STATES.FINISHED);
        handleRedirect();
    };

    const handleRedirect = () => {
        location.href = "/warenkorb/";
    };

    const handleOnClose = () => {
        setOpen(false);
        isAborted.current = true;
    };

    const handleNotAllowed = () => {
        setReorderState(REORDER_STATES.ERROR);
        setOpen(true);
    };

    const handleSubmitNewLocationData = (
        area_update,
        location_update,
        address_update
    ) => {
        const data = {
            area: area_update,
            location: location_update,
            address: address_update,
        };

        dispatchLocationChange(data);
        setOverlayOpen(false);
        const { store_id } = data.area;
        handleReorder(store_id);
    };

    /**
     * If store was not initilized we need
     * to open an address select overlay
     */
    const onButtonClickAction = useMemo(() => {
        if (!isStoreInit) {
            return () => setOverlayOpen(true);
        }
        return reoderAllowed ? () => handleReorder() : () => handleNotAllowed();
    }, [isStoreInit, reoderAllowed]);

    return (
        <>
            <Button
                icon={<ShoppingBag size={22} />}
                customStyle={`${
                    reoderAllowed ? "" : "disabled"
                } ${buttonStyle}`}
                label={i18next.t("reorder.CTA")}
                onClick={onButtonClickAction}
                size="LG"
            />
            <ReorderPopup
                {...{
                    order,
                    onClose: handleOnClose,
                    onRedirect: () => setOpen(false),
                    open,
                    store_id,
                    state: reorderState,
                }}
            />
            {addressOverlayOpen && (
                <AddressPopup
                    setOpen={setOverlayOpen}
                    submitData={handleSubmitNewLocationData}
                />
            )}
        </>
    );
};

ReorderContainer.propTypes = PopupContainerProps;

export default ReorderContainer;
